import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Eye, EyeOff, Lock, ShieldAlert, AlertCircle, ArrowLeft, ArrowRight,
  ShieldCheck, RotateCcw, Wifi, Crown, Tag, Wallet, Eye as EyeViewIcon,
} from "lucide-react";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OtpInput } from "@/components/OtpInput";
import { adminUsers, signInAsAdmin } from "@/lib/mock-data";
import { format } from "@/lib/brand-tokens";

// Map roles → icon + tone for the demo selector
const ROLE_META = {
  "Super Admin":        { icon: Crown,     tone: "bg-ink text-white" },
  "Deal Manager":       { icon: Tag,       tone: "bg-brand-700 text-white" },
  "Payments Overseer":  { icon: Wallet,    tone: "bg-orange-600 text-white" },
  "Read-only Operator": { icon: EyeViewIcon, tone: "bg-slate-500 text-white" },
};

// Pick a representative admin per role (FRS lists 4 roles; mock has 5 admins
// because Deal Manager has two — we use the first match for demo)
function adminForRole(role) {
  return adminUsers.find((a) => a.role === role);
}

// AdminLoginPage — austere, security-first, with demo role selector + mandatory MFA
export default function AdminLoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("credentials"); // credentials | mfa
  const [selectedRole, setSelectedRole] = useState("Super Admin");
  const [email, setEmail] = useState(adminForRole("Super Admin")?.email || "");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [code, setCode] = useState("");
  const [mfaError, setMfaError] = useState("");
  const otpRef = useRef(null);

  const codeReady = code.length === 6;

  // When demo role changes, pre-fill that admin's email
  const onPickRole = (role) => {
    setSelectedRole(role);
    const admin = adminForRole(role);
    if (admin) setEmail(admin.email);
    setError("");
  };

  const submitCredentials = (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Enter your email and password.");
      return;
    }
    // Demo: any password is accepted, but email must be a known admin
    if (!adminUsers.some((a) => a.email === email)) {
      setError("These credentials don't match an admin account.");
      return;
    }
    setStep("mfa");
  };

  const verifyMfa = (codeIn) => {
    if (codeIn === "000000") {
      setMfaError("That code didn't work. Codes refresh every 30 seconds.");
      otpRef.current?.reset();
      return;
    }
    // Pin the selected admin as the current session so AdminShell + every
    // admin-side page reflects the chosen role (not always Super Admin).
    const a = adminUsers.find((x) => x.email === email);
    if (a) signInAsAdmin(a.id);
    navigate("/admin/dashboard");
  };

  const reset = () => {
    otpRef.current?.reset();
    setMfaError("");
  };

  const admin = adminForRole(selectedRole);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="size-11 rounded-md bg-ink text-white grid place-items-center">
          <Lock className="size-5" />
        </div>
        <div>
          <div className="eyebrow text-ink-subtle">Admin portal</div>
          <h1 className="font-brand text-[32px] leading-[1.1] tracking-[-0.02em] text-ink font-bold">Admin sign-in</h1>
        </div>
      </div>

      {/* Security notice — only on credentials step */}
      {step === "credentials" && (
        <div className="bg-orange-50/40 border border-orange-200 rounded-md p-3.5 flex items-start gap-2.5">
          <ShieldAlert className="size-4 text-orange-700 mt-0.5 shrink-0" />
          <div className="text-caption text-ink-muted leading-relaxed">
            Admin sign-in is monitored. Every attempt is recorded with IP and timestamp.
            Five failed attempts within 15 minutes locks the account and notifies all Super Admins.
          </div>
        </div>
      )}

      {step === "credentials" ? (
        <>
          {/* DEMO ROLE SELECTOR — only on this prototype, would not exist in production */}
          <div className="border border-line rounded-md p-4 bg-surface-alt" data-testid="demo-role-selector">
            <div className="flex items-center justify-between mb-3">
              <div className="eyebrow">Demo · Choose a role</div>
              <span className="text-[10px] tracking-wider uppercase text-ink-hint">Prototype only</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {Object.keys(ROLE_META).map((role) => {
                const meta = ROLE_META[role];
                const Icon = meta.icon;
                const isActive = selectedRole === role;
                const a = adminForRole(role);
                return (
                  <button
                    key={role}
                    type="button"
                    onClick={() => onPickRole(role)}
                    data-testid={`demo-role-${role.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`text-left p-3 rounded-md border transition-all ${
                      isActive
                        ? "border-brand-700 bg-surface ring-1 ring-brand-700/20"
                        : "border-line bg-surface hover:border-ink"
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className={`size-6 rounded grid place-items-center shrink-0 ${meta.tone}`}>
                        <Icon className="size-3" strokeWidth={2.4} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-caption font-semibold text-ink truncate">{role}</div>
                        <div className="text-caption text-ink-subtle truncate">
                          {a ? `${a.firstName} ${a.lastName}` : "—"}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-caption text-ink-hint leading-relaxed">
              Selecting a role pre-fills the email below. Use any password &mdash; the prototype accepts everything.
            </p>
          </div>

          <form onSubmit={submitCredentials} className="space-y-5" data-testid="admin-login-form">
            <div>
              <Label className="text-eyebrow uppercase text-ink-muted mb-1.5 block">Admin email</Label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@gcbc.com" required autoFocus
                className="font-mono h-12 text-body bg-surface border-line" data-testid="admin-login-email"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-eyebrow uppercase text-ink-muted">Password</Label>
                <Link to="/auth/reset-password" className="text-caption font-semibold text-brand-700 hover:text-brand-800">Forgot?</Link>
              </div>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  className="pr-11 h-12 text-body bg-surface border-line" data-testid="admin-login-password"
                />
                <button type="button" onClick={() => setShowPwd((s) => !s)} aria-label={showPwd ? "Hide password" : "Show password"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 size-8 grid place-items-center text-ink-hint hover:text-ink rounded-md hover:bg-surface-alt transition-colors">
                  {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-caption text-red-700">
                <AlertCircle className="size-3.5 mt-0.5 shrink-0" />{error}
              </div>
            )}

            <Button type="submit"
                    className="w-full h-12 bg-ink hover:bg-slate-800 text-body-sm font-semibold text-white"
                    data-testid="admin-login-submit-btn">
              Continue to two-factor <ArrowRight className="size-4 ml-2" />
            </Button>
          </form>

          {/* Footer line — security info only (admin sign-in is a dedicated URL) */}
          <div className="border-t border-line pt-5 grid sm:grid-cols-2 gap-3 text-caption">
            <div className="flex items-start gap-2 text-ink-hint">
              <Wifi className="size-3.5 mt-0.5 shrink-0" />
              <span>Sign-in restricted to allowlisted IPs in production</span>
            </div>
            <div className="flex items-start gap-2 text-ink-hint">
              <ShieldCheck className="size-3.5 mt-0.5 shrink-0" />
              <span>30-minute idle timeout · MFA every session</span>
            </div>
          </div>
        </>
      ) : (
        <>
          {/* MFA step */}
          <div className="bg-surface-alt border border-line rounded-md px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-caption text-ink-subtle">Signing in as</div>
              <div className="text-body-sm text-ink font-semibold">
                {admin?.firstName} {admin?.lastName} <span className="text-ink-hint font-normal">· {admin?.role}</span>
              </div>
            </div>
            <button onClick={() => { setStep("credentials"); otpRef.current?.reset(); setMfaError(""); setCode(""); }}
                    className="text-caption text-ink-muted hover:text-ink inline-flex items-center gap-1">
              <ArrowLeft className="size-3.5" /> Back
            </button>
          </div>

          <div>
            <h2 className="font-brand text-h2 text-ink">Enter your authenticator code</h2>
            <p className="mt-2 text-caption text-ink-subtle leading-relaxed">
              Open your TOTP app and enter the current 6-digit code. MFA is mandatory on every admin session.
            </p>
          </div>

          <OtpInput
            ref={otpRef}
            length={6}
            testidPrefix="admin-mfa"
            onChange={(c) => { setCode(c); if (c.length > 0) setMfaError(""); }}
            onComplete={verifyMfa}
          />

          {mfaError && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-md text-caption text-red-700">
              <AlertCircle className="size-3.5 mt-0.5 shrink-0" />{mfaError}
            </div>
          )}

          <Button
            onClick={() => verifyMfa(code)}
            disabled={!codeReady}
            data-testid="admin-mfa-verify-btn"
            className="w-full h-11 bg-ink hover:bg-slate-800 text-white"
          >
            Verify and continue <ArrowRight className="size-4 ml-2" />
          </Button>

          <div className="text-center text-caption space-x-3">
            <span className="text-ink-hint">Demo: any 6 digits except <span className="font-mono">000000</span>.</span>
            <button onClick={reset} className="text-ink-muted hover:text-ink inline-flex items-center gap-1">
              <RotateCcw className="size-3" /> Reset
            </button>
          </div>

          <div className="border-t border-line pt-4 text-center text-caption text-ink-subtle">
            Lost your authenticator?{" "}
            <Link to="/contact" className="text-brand-700 font-semibold hover:text-brand-800">Contact a Super Admin</Link>
          </div>
        </>
      )}
    </div>
  );
}
